import { I18n } from '@calendly/i18n';
import { Badge } from '@calendly/ui/components/badge';
import { Button } from '@calendly/ui/components/button';
import { AiSparkleFilledIcon, AiSparkleIcon } from '@calendly/ui/components/icons';
import { InlineBanner } from '@calendly/ui/components/inline-banner';
import { BodyText, LabelText, TitleText } from '@calendly/ui/components/typography';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useGetUserQuery } from 'common';
import { useTrack, useTrackOnce } from 'hooks/analytics_events';
import { useFetchSchedulingAssistantSettingsQuery, SCHEDULING_ASSISTANT_ENABLED_SETTING_NAME } from 'hooks/scheduling_assistant/api';
import { getSchedulingAssistantEnabledSettingValueByName } from 'hooks/scheduling_assistant/helpers';
import { bannerShownEvent, trackDismissed, trackTryItOutClicked, trackTrySchedulingWithAiClicked } from './analytics';
import callieEmailSchedulerBannerPng from './media/callie-email-scheduler-banner.png';
export const AI_ASSISTANT_BANNER_DISMISSED_KEY = 'ai_assistant_banner_dismissed';
const inlineBannerOverrideCss = "i1kvevlc";
const inlineBannerOverridePhoneCss = "ih76kl8";
const messageLayoutCss = "m1iaqhev";
const messageLayoutPhoneCss = "mrkfzya";
const contentColumnCss = "c1xotjmq";
const headlineBlockCss = "h1gs69ei";
const titleRowCss = "t1gy7la8";
const messageTextCss = "m16481w9";
const primaryCtaContentCss = "pdfmxyz";
const illustrationWrapperCss = "i1gdfihb";
const illustrationImageCss = "iub6ew2";
const useGetUserSchedulingAssistantEnabledSettingValue = () => {
  const {
    data: userData,
    isLoading: userIsLoading,
    isError: userIsError
  } = useGetUserQuery();
  const {
    uuid: userUuid
  } = userData ?? {};
  const {
    data: settings,
    isLoading: settingsIsLoading,
    isError: settingsIsError
  } = useFetchSchedulingAssistantSettingsQuery(userUuid, {
    skip: !userUuid
  });
  const schedulingAssistantEnabledSettingValue = getSchedulingAssistantEnabledSettingValueByName(settings, SCHEDULING_ASSISTANT_ENABLED_SETTING_NAME);
  return {
    isLoading: userIsLoading || settingsIsLoading,
    isError: userIsError || settingsIsError,
    schedulingAssistantEnabledSettingValue
  };
};
export const AIAssistantButton = () => {
  const track = useTrack();
  const history = useHistory();
  const {
    isLoading,
    isError,
    schedulingAssistantEnabledSettingValue
  } = useGetUserSchedulingAssistantEnabledSettingValue();
  const assistantIsEnabled = schedulingAssistantEnabledSettingValue === 'true';
  const assistantIsDisabled = schedulingAssistantEnabledSettingValue === 'false';
  if (assistantIsDisabled || isLoading || isError) return null;
  const buttonLabel = assistantIsEnabled ? I18n.t('publisher.home.ai_assistant_button.setting_enabled') : I18n.t('publisher.home.ai_assistant_button.no_setting_configured');
  const handleClickTrySchedulingWithAi = () => {
    trackTrySchedulingWithAiClicked(track);
    setTimeout(() => history.push('/app/personal/assistant'), 300);
  };
  const cc = I18n.t('publisher.personal_settings.scheduling_assistant.email_address');
  const body = encodeURIComponent(I18n.t('publisher.home.ai_assistant_button.schedule_with_callie_email_body'));
  const buttonProps = assistantIsEnabled ? {
    as: 'a',
    href: `mailto:?cc=${cc}&body=${body}`,
    target: '_blank',
    rel: 'noopener noreferrer'
  } : {
    onClick: handleClickTrySchedulingWithAi
  };
  return <Button decoration="ai" {...buttonProps}>
      {buttonLabel}
    </Button>;
};
export const AIAssistantBanner = ({
  onDismiss
}) => {
  const track = useTrack();
  const history = useHistory();
  const isPhone = useDeviceType('isPhone');
  const {
    isLoading,
    isError,
    schedulingAssistantEnabledSettingValue
  } = useGetUserSchedulingAssistantEnabledSettingValue();
  const assistantIsConfigured = !!schedulingAssistantEnabledSettingValue;
  useEffect(() => {
    if (isLoading || isError) return;
    if (!assistantIsConfigured) return;
    // User has configured the assistant from the settings page directly, so we don't need to show the banner
    localStorage.setItem(AI_ASSISTANT_BANNER_DISMISSED_KEY, 'true');
    onDismiss();
  }, [isLoading, isError, assistantIsConfigured, onDismiss]);
  const shouldShowBanner = !isLoading && !isError && !assistantIsConfigured;
  useTrackOnce(shouldShowBanner, bannerShownEvent);
  if (!shouldShowBanner) return null;
  const handleClick = () => {
    trackTryItOutClicked(track);
    localStorage.setItem(AI_ASSISTANT_BANNER_DISMISSED_KEY, 'true');
    onDismiss();
    setTimeout(() => history.push('/app/personal/assistant'), 300);
  };
  const handleInlineBannerDismiss = () => {
    trackDismissed(track);
    localStorage.setItem(AI_ASSISTANT_BANNER_DISMISSED_KEY, 'true');
    onDismiss();
  };
  return <div data-testid="ai-assistant-banner">
      <InlineBanner className={classNames(inlineBannerOverrideCss, isPhone && inlineBannerOverridePhoneCss)} variant="neutral" hasIcon={false} isDismissible onDismiss={handleInlineBannerDismiss} message={<div className={classNames(messageLayoutCss, isPhone && messageLayoutPhoneCss)}>
            <div className={contentColumnCss}>
              <div className={headlineBlockCss}>
                <div className={titleRowCss}>
                  <AiSparkleFilledIcon size="standard" useGradient aria-hidden />
                  <TitleText size="medium" level="3">
                    {I18n.t('publisher.home.ai_assistant_banner.title')}
                  </TitleText>
                  <Badge type="new">Beta</Badge>
                </div>
                <BodyText className={messageTextCss} as="div">
                  {I18n.t('publisher.home.ai_assistant_banner.message_before')}
                  <BodyText as="span" weight="semibold" inline>
                    {I18n.t('publisher.home.ai_assistant_banner.message_emphasis')}
                  </BodyText>
                  {I18n.t('publisher.home.ai_assistant_banner.message_after')}
                </BodyText>
              </div>
              <div>
                <Button decoration="ai" onClick={handleClick} size="small">
                  <span className={primaryCtaContentCss}>
                    <AiSparkleIcon size="small" aria-hidden />
                    <LabelText size="large">
                      {I18n.t('publisher.home.ai_assistant_banner.button')}
                    </LabelText>
                  </span>
                </Button>
              </div>
            </div>
            <div className={illustrationWrapperCss}>
              <img className={illustrationImageCss} data-testid="ai-assistant-banner-illustration" src={callieEmailSchedulerBannerPng} alt="" />
            </div>
          </div>} />
    </div>;
};

require("./ai_assistant_banner.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./ai_assistant_banner.js");